import { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { SlotReel, spinTotal } from '@/components/slot-reel';
import type { Cohort } from '@/game/types';
import { colors, radius, type } from '@/theme';

/** Long cohort names step down a size rather than truncating. */
const cohortNameSize = (name: string) => {
  if (name.length <= 8) return { fontSize: 44, lineHeight: 48 };
  if (name.length <= 11) return { fontSize: 38, lineHeight: 42 };
  if (name.length <= 14) return { fontSize: 32, lineHeight: 36 };
  if (name.length <= 18) return { fontSize: 27, lineHeight: 31 };
  if (name.length <= 23) return { fontSize: 23, lineHeight: 27 };
  return { fontSize: 20, lineHeight: 24 };
};

const nameLines = (name: string) => (name.length > 18 ? 2 : 1);

const FACE_GAP = 8;
const ERA_LINE_HEIGHT = 14;

function CohortFace({ cohort, lines }: { cohort: Cohort; lines: number }) {
  return (
    <View style={styles.face}>
      {/* The ramp picks the size. Don't add adjustsFontSizeToFit here — with an
          explicit lineHeight and negative tracking iOS ignores minimumFontScale
          and shrinks the name to near-nothing. */}
      <Text
        style={[styles.cohortName, cohortNameSize(cohort.name), { color: cohort.accent }]}
        numberOfLines={lines}
        allowFontScaling={false}>
        {cohort.name}
      </Text>
      <Text style={styles.cohortEra} allowFontScaling={false}>
        {cohort.era} · PICK ONE
      </Text>
    </View>
  );
}

type Props = {
  cohort: Cohort;
  /** Cohorts the reel rushes past before landing. Omit to skip the spin. */
  decoys?: Cohort[];
  /** When the reel starts, measured from mount. */
  spinDelay?: number;
};

/**
 * The cohort headline as a slot reel — it spins through other cohorts and
 * thunks onto the one you're drafting from. Remounted per draw by its key.
 */
export function CohortBanner({ cohort, decoys, spinDelay = 0 }: Props) {
  const reducedMotion = useReducedMotion();
  const lines = nameLines(cohort.name);
  const height = cohortNameSize(cohort.name).lineHeight * lines + FACE_GAP + ERA_LINE_HEIGHT;

  // The banner takes the hit when the reel lands, so the stop reads as physical.
  const pop = useSharedValue(1);
  useEffect(() => {
    if (reducedMotion || !decoys?.length) return;
    pop.value = withDelay(
      Math.max(spinTotal(spinDelay) - 120, 0),
      withSequence(
        withTiming(1.035, { duration: 110 }),
        withTiming(1, { duration: 170 }),
      ),
    );
  }, [decoys?.length, pop, reducedMotion, spinDelay]);

  const popStyle = useAnimatedStyle(() => ({ transform: [{ scale: pop.value }] }));

  return (
    <Animated.View style={[styles.banner, { borderColor: cohort.accent }, popStyle]}>
      {decoys && decoys.length > 0 ? (
        <SlotReel
          style={styles.reel}
          height={height}
          delay={spinDelay}
          landed={<CohortFace cohort={cohort} lines={lines} />}
          decoys={decoys.map((decoy) => (
            <CohortFace key={decoy.id} cohort={decoy} lines={1} />
          ))}
        />
      ) : (
        <CohortFace cohort={cohort} lines={lines} />
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: {
    borderWidth: 1,
    borderRadius: radius.lg,
    backgroundColor: colors.card,
    paddingVertical: 20,
    paddingHorizontal: 18,
    alignItems: 'center',
    marginBottom: 4,
  },
  reel: {
    alignSelf: 'stretch',
  },
  face: {
    alignItems: 'center',
    gap: FACE_GAP,
  },
  cohortName: {
    ...type.display,
    fontSize: 42,
    lineHeight: 46,
    textAlign: 'center',
  },
  cohortEra: {
    ...type.label,
    fontSize: 11,
    lineHeight: ERA_LINE_HEIGHT,
    color: colors.dim,
  },
});
